import Vue from 'vue'
import EventBus from '@/plugins/EventBus'
import {
  HOSPITAIS_CURRENT_USER_LOADED_EVENT,
  HOSPITAIS_CURRENT_USER_STORAGE_KEY,
} from '@/composables/useHospitais/useHospitais'

/**
 * Integracao com o GetDemo.
 *
 * O script da ferramenta e carregado em public/getdemo.js (incluido no
 * index.html) e expoe `window.Getdemo`. Este plugin identifica o usuario
 * logado para o GetDemo, enviando o id do usuario e atributos uteis derivados
 * do token (perfil, ambiente, etc.) via `window.Getdemo.identify(userId, params)`.
 *
 * Tudo e best-effort: qualquer falha aqui e silenciada para nunca quebrar o
 * boot da aplicacao. Caso o usuario nao esteja autenticado, ou o GetDemo nao
 * esteja habilitado (sem GETDEMO_COMPANY_ID), nada acontece.
 */

declare global {
  interface Window {
    GETDEMO_COMPANY_ID?: string;
    Getdemo?: {
      q?: any[];
      identify: (userId: string, params?: Record<string, any>) => void;
      trigger: (itemId: string, redirectUrl?: string) => void;
    };
  }
}

// Aguarda o stub do GetDemo (criado em public/getdemo.js) ficar disponivel.
// Em condicoes normais ja existe quando o bundle roda; isto e apenas defensivo.
function waitForGetdemo(timeoutMs = 5000): Promise<Window['Getdemo'] | null> {
  return new Promise((resolve) => {
    if (window.Getdemo && typeof window.Getdemo.identify === 'function') {
      resolve(window.Getdemo)
      return
    }
    const start = Date.now()
    const interval = window.setInterval(() => {
      if (window.Getdemo && typeof window.Getdemo.identify === 'function') {
        window.clearInterval(interval)
        resolve(window.Getdemo)
      } else if (Date.now() - start > timeoutMs) {
        window.clearInterval(interval)
        resolve(null)
      }
    }, 100)
  })
}

// Classifica o usuario em um tipo principal para facilitar o targeting no GetDemo.
// Regras validadas com o time de produto:
//   - backoffice tem prioridade sobre todos os outros perfis
//   - secretariaDoMedico = agendamento-medico + role assinarEnviar (único diferenciador do médico)
function deriveTipoUsuario(roles: string[], pageAuthorized: string[]): string {
  if (
    pageAuthorized.some((p) => p.startsWith('backoffice-')) ||
    roles.includes('secretariaDoAgendamentoBackoffice') ||
    roles.includes('secretariaDoAmbulatorioBackoffice')
  ) return 'backoffice'

  if (pageAuthorized.includes('agendamento-medico')) {
    return roles.includes('assinarEnviar') ? 'secretariaDoMedico' : 'medico'
  }

  if (pageAuthorized.some((p) => p.startsWith('financeiro-') || p === 'financeiro')) return 'financeiro'
  if (pageAuthorized.some((p) => p.includes('operadora'))) return 'operadora'
  if (pageAuthorized.includes('suporte')) return 'suporte'

  return 'outro'
}

// Le do cache em localStorage (alimentado pelo useHospitais quando alguma tela
// do Agendamento carrega a lista) os ids de hospitais do usuario. So usa o
// cache se ele pertencer ao proprio usuario (sub), cobrindo troca de usuario e
// personificacao. Retorna null se nao houver cache valido.
function readHospitaisIdsFromStorage(sub: string): string[] | null {
  try {
    const raw = localStorage.getItem(HOSPITAIS_CURRENT_USER_STORAGE_KEY)
    if (!raw) return null
    const cached = JSON.parse(raw)
    if (!cached || cached.sub !== sub || !Array.isArray(cached.ids)) return null
    return cached.ids
  } catch (e) {
    return null
  }
}

// Monta os atributos do identify usando SOMENTE dados ja presentes no token ou
// no storage do usuario logado. O GetDemo nao deve disparar nenhuma request a
// APIs do portal: alem do custo, usuarios sem acesso ao modulo consultado
// receberiam 403/500 (ex.: usuario de remuneracao medica x endpoint de
// hospitais do Agendamento).
function buildParams(profile: any): Record<string, any> {
  const roles = Array.isArray(profile.roles) ? profile.roles : []
  const pageAuthorized = Array.isArray(profile.pageAuthorized) ? profile.pageAuthorized : []
  const hospitaisIds = readHospitaisIdsFromStorage(profile.sub)
  return {
    nome: profile.name,
    email: profile.email,
    usuario: profile.preferredUsername,
    // Privacidade/LGPD: NAO enviamos a lista de roles (perfis), permissoes nem
    // paginasAutorizadas ao terceiro. Para segmentacao basta a classificacao
    // de alto nivel abaixo. Os roles sao usados apenas localmente para deriva-la.
    tipoUsuarioPrincipal: deriveTipoUsuario(roles, pageAuthorized),
    // login_AD é um claim customizado do Keycloak que indica autenticação via Azure AD.
    // Após objectKeysToCamelCase vira loginAd (lodash _.camelCase).
    loginAd: profile.loginAd === 'true' || profile.loginAd === true,
    // authTime = timestamp do login. Útil para fluxos de onboarding (ex.: não exibir
    // tour para usuários que logam há mais de X dias).
    authTime: profile.authTime,
    // mfaAtivo removido por enquanto: o claim has_mfa ainda nao existe no token
    // (depende de um Protocol Mapper no Keycloak). Reintroduzir quando o claim existir.
    ambiente: window.ENV,
    // Hospitais vem do cache local (sem request). Se ainda nao ha cache para o
    // usuario, o campo fica de fora do identify. Enviado como texto separado
    // por virgula porque o GetDemo nao ingere atributos do tipo array — a
    // segmentacao deve usar "contem <id>".
    ...(hospitaisIds ? { hospitaisIds: hospitaisIds.join(', ') } : {}),
  }
}

// Aguarda o profile do usuario logado ficar disponivel em Vue.prototype.$user.
// No fluxo normal de login ele ja existe no boot (o token e gravado em
// callback.html antes do reload da SPA). Este wait cobre casos em que o profile
// chega depois (ex.: troca de usuario por personificacao, que atualiza $user de
// forma reativa, sem recarregar a pagina).
function waitForProfile(timeoutMs = 5000): Promise<any | null> {
  const getProfile = () => {
    const user: any = (Vue.prototype as any).$user
    const profile = user && user.profile
    return profile && profile.sub ? profile : null
  }
  return new Promise((resolve) => {
    const immediate = getProfile()
    if (immediate) {
      resolve(immediate)
      return
    }
    const start = Date.now()
    const interval = window.setInterval(() => {
      const profile = getProfile()
      if (profile) {
        window.clearInterval(interval)
        resolve(profile)
      } else if (Date.now() - start > timeoutMs) {
        window.clearInterval(interval)
        resolve(null)
      }
    }, 100)
  })
}

async function identifyCurrentUser() {
  try {
    if (!window.GETDEMO_COMPANY_ID) return

    // Sem usuario autenticado (ex.: tela de login) nao ha o que identificar.
    const profile = await waitForProfile()
    if (!profile) return

    const getdemo = await waitForGetdemo()
    if (!getdemo) return

    const params = buildParams(profile)
    getdemo.identify(profile.sub, params)
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[GetDemo] falha ao identificar usuario', e)
  }
}

const GetDemo = {
  install() {
    if (typeof window === 'undefined') return
    identifyCurrentUser()

    // Quando alguma tela carregar a lista de hospitais durante a sessao (o
    // useHospitais persiste os ids no localStorage e emite este evento),
    // reenvia o identify ja com os hospitais. Cobre a primeira sessao do
    // usuario, em que o cache ainda nao existia no boot.
    EventBus.$on(HOSPITAIS_CURRENT_USER_LOADED_EVENT, () => {
      identifyCurrentUser()
    })
  },
}

export default GetDemo
